"use client";

import { createPortal } from "react-dom";
import { useState, useRef, type ReactNode } from "react";

export function NavBadge({ count, expanded }: { count?: number; expanded: boolean }) {
  if (!count) return null;
  const text = count > 9 ? "9+" : count;
  
  if (expanded) {
    return (
      <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-medium leading-none text-white">
        {text}
      </span>
    );
  }
  
  return (
    <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-xs font-medium leading-none text-white ring-2 ring-white dark:ring-black group-hover:ring-slate-50 dark:group-hover:ring-neutral-900">
      {text}
    </span>
  );
}

export function FadeLabel({ expanded, children }: { expanded: boolean; children: ReactNode }) {
  return (
    <span
      className={`overflow-hidden whitespace-nowrap transition-opacity duration-150 ${
        expanded ? "opacity-100 delay-150 " : "w-0 opacity-0 delay-0"
      }`}
    >
      {children}
    </span>
  );
}

export function SidebarTooltip({ label, expanded, children }: { label: string; expanded: boolean; children: ReactNode }) {
  const [visible, setVisible] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  if (expanded) return <>{children}</>;

  const show = () => {
    if (wrapperRef.current) {
      setRect(wrapperRef.current.getBoundingClientRect());
      setVisible(true);
    }
  };
  const hide = () => setVisible(false);

  return (
    <div
      ref={wrapperRef}
      className="relative"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {visible && rect && typeof document !== "undefined"
        ? createPortal(
            <span
              className="pointer-events-none fixed z-overlay rounded-md bg-slate-900 px-2.5 py-1.5 text-xs font-medium whitespace-nowrap text-white shadow-xl dark:bg-neutral-800 dark:text-neutral-100"
              style={{
                left: rect.right + 8,
                top: rect.top + rect.height / 2,
                transform: "translateY(-50%)",
              }}
            >
              {label}
            </span>,
            document.body
          )
        : null}
    </div>
  );
}
